import { styled } from 'baseui'
import { ChevronDown } from 'baseui/icon'

import { FloatingLabel as FloatingLabelBase } from '@/shared/theme/styled-components'

export const FloatingLabel = styled(FloatingLabelBase, {
  padding: '0 4px',
  zIndex: 1,
})

const pulseBorder = color => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

const pulseRadius = radius => ({
  borderTopLeftRadius: radius,
  borderTopRightRadius: radius,
  borderBottomLeftRadius: radius,
  borderBottomRightRadius: radius,
})

const pulseControlState = ({ $theme, $isFocused, $disabled, $error }, missing) => {
  const { colors } = $theme
  if ($disabled) return { ...pulseBorder(colors.borderPulse), backgroundColor: colors.bgPulseTrack }
  if ($isFocused) {
    return {
      ...pulseBorder(colors.primaryPulse),
      backgroundColor: colors.white,
      boxShadow: $theme.lighting.shadowPulseFocus,
    }
  }
  if ($error) return { ...pulseBorder(colors.dangerPulse), backgroundColor: colors.dangerPulseBg }
  if (missing) return { ...pulseBorder(colors.missPulseBorder), backgroundColor: colors.missPulseBg }

  return {
    ...pulseBorder(colors.borderPulse),
    backgroundColor: colors.white,
    ':hover': pulseBorder(colors.borderPulseHover),
  }
}

export const SDSelectPulseOverrides = (missing = false) => ({
  ControlContainer: {
    style: props => ({
      alignItems: 'center',
      height: '36px',
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      outline: 'none',
      ...pulseRadius('8px'),
      color: props.$disabled ? props.$theme.colors.textPulseFaint : props.$theme.colors.textPulseStrong,
      cursor: props.$disabled ? 'not-allowed' : 'pointer',
      ...pulseControlState(props, missing),
    }),
  },
  ValueContainer: {
    style: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '12px',
      paddingRight: '6px',
    },
  },
  Placeholder: {
    style: ({ $theme }) => ({
      color: $theme.colors.textPulsePlaceholder,
      fontSize: '14px',
    }),
  },
  SingleValue: {
    style: ({ $theme, $disabled }) => ({
      color: $disabled ? $theme.colors.textPulseFaint : $theme.colors.textPulseStrong,
      fontSize: '14px',
      lineHeight: '20px',
    }),
  },
  IconsContainer: {
    style: {
      paddingRight: '8px',
    },
  },
  SearchIconContainer: {
    style: ({ $theme }) => ({
      paddingLeft: '12px',
      color: $theme.colors.textPulseFaint,
    }),
  },
  ClearIcon: {
    props: {
      overrides: {
        Svg: {
          style: ({ $theme }) => ({
            color: $theme.colors.textPulseFaint,
          }),
        },
      },
    },
  },
  Input: {
    props: {
      autoComplete: 'off',
    },
  },
  SelectArrow: {
    component: ChevronDown,
    props: {
      size: 18,
      overrides: {
        Svg: {
          style: ({ $theme }) => ({
            color: $theme.colors.textPulseMuted,
          }),
        },
      },
    },
  },
  Popover: {
    props: {
      overrides: {
        Body: {
          style: () => ({
            marginTop: '4px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }),
        },
        Inner: {
          style: () => ({
            backgroundColor: 'transparent',
          }),
        },
      },
    },
  },
  DropdownContainer: {
    style: () => ({
      backgroundColor: 'transparent',
    }),
  },
  StatefulMenu: {
    props: {
      overrides: {
        List: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.white,
            ...pulseBorder($theme.colors.borderPulse),
            ...pulseRadius('10px'),
            boxShadow: $theme.lighting.shadowPulseMenu,
            paddingTop: '4px',
            paddingRight: '4px',
            paddingBottom: '4px',
            paddingLeft: '4px',
          }),
        },
        Option: {
          props: {
            overrides: {
              ListItem: {
                style: ({ $theme, $isHighlighted }) => ({
                  minHeight: '34px',
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  outline: 'none',
                  ...pulseRadius('6px'),
                  backgroundColor: $isHighlighted ? $theme.colors.bgPulseTrack : 'transparent',
                }),
              },
            },
          },
        },
      },
    },
  },
  OptionContent: {
    style: ({ $theme, $selected }) => ({
      display: 'flex',
      alignItems: 'center',
      minHeight: '34px',
      paddingTop: 0,
      paddingRight: '10px',
      paddingBottom: 0,
      paddingLeft: '10px',
      fontSize: '14px',
      color: $selected ? $theme.colors.primaryPulse : $theme.colors.textPulseStrong,
      fontWeight: $selected ? 600 : 400,
      backgroundColor: $selected ? $theme.colors.bgPulseSelected : 'transparent',
      ...pulseRadius('6px'),
    }),
  },
})

export const SDSelectOverrides = () => ({
  ControlContainer: {
    style: ({ $theme, $isFocused, $disabled, $error }) => {
      const borderColor = $disabled ? $theme.colors.mono300 : $error ? $theme.colors.negative500 : $theme.colors.mono300
      const outlineColor = $disabled
        ? $theme.colors.mono300
        : $error
          ? $theme.colors.negative500
          : $theme.colors.primary500
      const textColor = $disabled ? $theme.colors.mono600 : $theme.colors.mono700
      const bgColor = $disabled ? $theme.colors.mono100 : $theme.colors.white

      return {
        alignItems: 'center',
        height: '3.0rem',
        paddingTop: '20px',
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        borderTopWidth: '0.0625rem',
        borderRightWidth: '0.0625rem',
        borderBottomWidth: '0.0625rem',
        borderLeftWidth: '0.0625rem',
        outline: $disabled || !($isFocused || $error) ? 'none' : `0.125rem solid ${outlineColor}`,
        outlineOffset: $isFocused || $error ? '-.125rem' : 0,
        color: textColor,
        borderTopColor: borderColor,
        borderRightColor: borderColor,
        borderBottomColor: borderColor,
        borderLeftColor: borderColor,
        backgroundColor: bgColor,
        cursor: $disabled ? 'not-allowed' : 'pointer',
      }
    },
  },
  Input: {
    props: {
      autoComplete: 'off',
    },
  },
  Popover: {
    props: {
      overrides: {
        Body: {
          style: () => ({
            left: '1px',
            marginTop: '.5rem',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }),
        },
        Inner: {
          style: () => ({
            backgroundColor: 'transparent',
          }),
        },
      },
    },
  },
  DropdownContainer: {
    style: () => ({
      backgroundColor: 'transparent',
    }),
  },
  SelectArrow: {
    props: {
      overrides: {
        Svg: {
          style: {
            height: '100%',
            width: '25px',
            transform: 'translateY(-40%)',
          },
        },
      },
    },
    component: ChevronDown,
  },
  StatefulMenu: {
    props: {
      overrides: {
        List: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.white,
            border: `.0625rem solid ${$theme.colors.mono300}`,
            borderTopRightRadius: $theme.borders.radius200,
            borderBottomRightRadius: $theme.borders.radius200,
            borderBottomLeftRadius: $theme.borders.radius200,
            borderTopLeftRadius: $theme.borders.radius200,
            paddingTop: '1rem',
            paddingRight: '1rem',
            paddingBottom: '1rem',
            paddingLeft: '1rem',
          }),
        },
        Option: {
          props: {
            overrides: {
              ListItem: {
                style: ({ $theme, $isHighlighted }) => {
                  const outlineStyle = $isHighlighted ? `0.125rem solid ${$theme.colors.primary500}` : 'none'
                  return {
                    minHeight: '2rem',
                    paddingTop: '0',
                    paddingRight: '0',
                    paddingBottom: '0',
                    paddingLeft: '0',
                    outline: outlineStyle,
                    outlineOffset: '-.125rem',
                    color: $theme.colors.mono700,
                    backgroundColor: $theme.colors.white,
                    borderTopRightRadius: $theme.borders.radius200,
                    borderBottomRightRadius: $theme.borders.radius200,
                    borderBottomLeftRadius: $theme.borders.radius200,
                    borderTopLeftRadius: $theme.borders.radius200,
                    ':not(:last-child)': {
                      marginBottom: '.5rem',
                    },
                  }
                },
              },
            },
          },
        },
      },
    },
  },
  OptionContent: {
    style: ({ $theme, $selected }) => {
      const bgColour = $selected ? $theme.colors.bg100 : $theme.colors.white
      return {
        minHeight: '2rem',
        paddingTop: '.25rem',
        paddingRight: '.5rem',
        paddingBottom: '.25rem',
        paddingLeft: '.5rem',
        color: $selected ? $theme.colors.primary500 : 'inherit',
        fontWeight: 'normal',
        backgroundColor: bgColour,
        borderTopRightRadius: $theme.borders.radius200,
        borderBottomRightRadius: $theme.borders.radius200,
        borderBottomLeftRadius: $theme.borders.radius200,
        borderTopLeftRadius: $theme.borders.radius200,
      }
    },
  },
})
